import {
  BOOLEAN_SELECT_LIST_ITEMS,
  Box,
  Card,
  CrudMode,
  DataDisplay,
  Grid,
  GridBreak,
  Stack,
  Typography,
  ValidationErrors,
  formatBoolean,
  formatDate,
  formatSchoolYear,
  pn,
  useElementBreakpoints,
} from "@compass/core";
import { CustomDataModelFieldDisplay, CustomDataModelFieldEditor, DatePicker, Input, Select } from "components";
import {
  LkEndReasonTypeId,
  LkStudentEnrollmentDisplayFieldId,
  StudentCustomIdType,
  StudentEnrollment,
  StudentEnrollmentEditResource,
  StudentEnrollmentViewResource,
} from "generated/openApi";
import produce from "immer";
import { formatAdvisorName } from "../formatAdvisorName";
import "moment-timezone";
import ApiService from "services/apiService";

export const StudentEnrollmentPageBody = (props: {
  crudMode: CrudMode;
  enrollment: StudentEnrollment;
  setEnrollment: (value: StudentEnrollment) => void;
  viewResource: StudentEnrollmentViewResource;
  editResource: StudentEnrollmentEditResource;
  validationErrors: ValidationErrors | string[];
  hideStudent?: boolean;
}) => {
  const properties = props.viewResource.properties || [];
  const { breakpointsRef, smallFieldBreakpoints } = useElementBreakpoints();

  const displayFields = props.viewResource.displayFields || [];
  const startDateDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.StartDate);
  const schoolDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.School);
  const collegeDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.College);
  const advisorDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.Advisor);
  const enrollmentStatusDisplayField = displayFields.find(
    (x) => x.id === LkStudentEnrollmentDisplayFieldId.EnrollmentStatus
  );
  const creditsDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.Credits);
  const dependentStatusDisplayField = displayFields.find(
    (x) => x.id === LkStudentEnrollmentDisplayFieldId.DependentStatus
  );
  const nihDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.Nih);
  const agiDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.Agi);
  const unmetNeedDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.UnmetNeed);
  const loanAmountDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.LoanAmount);
  const pellRecipientDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.PellRecipient);
  const gradeLevelDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.GradeLevel);
  const endGradeLevelDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.EndGradeLevel);
  const participantLevelDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.ParticipantLevel);
  const dualEnrollmentParticipationDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.DualEnrollmentParticipation);
  const dualEnrollmentCredentialsDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.DualEnrollmentCredentials);
  const workStudyDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.WorkStudy);
  const employmentDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.EmploymentUB);
  const culturalDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.Cultural);
  const communityServiceDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.CommunityService);
  const lepInstructionDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.LEPInstruction);
  const diplomaTypeDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.DiplomaType);
  const iepDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.IEP);
  const lepDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.LEP);
  const initialEnrollmentStatusDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.InitialEnrollmentStatusId);
  const finalEnrollmentStatusDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.FinalEnrollmentStatusId);
  const participantStatusDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.ParticipantStatusId);
  const persistantStatusDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.PersistantStatusId);
  const militaryConnectionDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.MilitaryConnectionId);
  const freeReducedLunchDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.FreeReducedLunch);
  const magnetDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.Magnet);
  const heldBackDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.HeldBack);
  const avidDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.Avid);
  const mediaReleaseDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.MediaRelease);
  const cacgDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.Cacg);
  const twentyFirstCenturyDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.TwentyFirstCentury);
  const dropTransferFormDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.DropTransferForm);
  const homelessDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.Homeless);
  const fosterCareDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.FosterCare);
  const atRiskDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.AtRisk);
  const ubDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.Ub);
  const ubmsDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.Ubms);
  const vubDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.Vub);
  const tsDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.Ts);
  const sssDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.Sss);
  const gearUpDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.GearUp);
  const eocDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.Eoc);

  const academicStandingDisplayField = displayFields.find(
    (x) => x.id === LkStudentEnrollmentDisplayFieldId.AcademicStanding
  );
  const ccpDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.Ccp);
  const rigorousCDisplayField = displayFields.find((x) => x.id === LkStudentEnrollmentDisplayFieldId.RigorousC);
  const dualEnrollmentDisplayField = displayFields.find(
    (x) => x.id === LkStudentEnrollmentDisplayFieldId.DualEnrollment
  );

  const showGradeLevelTerm = props.viewResource.gradeLevels?.find(
    (x) => x.id === props.enrollment.gradeLevelId
  )?.displayStudentGradeLevelTerm;

  const graduatedEndReasons = props.viewResource.endReasons?.filter(
    (x) => x.endReasonTypeId == LkEndReasonTypeId.Graduated || x.endReasonTypeId == LkEndReasonTypeId.GraduatedWithRigorous || x.endReasonTypeId == LkEndReasonTypeId.GraduatedWithoutRigorous
  );

  const dependentStatusField = () => {
    const propertyName = pn<StudentEnrollment>("dependentStatusId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={dependentStatusDisplayField?.displayName}>
        {props.enrollment.dependentStatus?.name}
      </DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={dependentStatusDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.dependentStatuses?.find((x) => x.id === props.enrollment.dependentStatusId)}
        options={props.editResource.dependentStatuses}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.dependentStatusId = value?.id;
            })
          )
        }
      />
    );
  };

  const studentField = () => {
    const propertyName = pn<StudentEnrollment>("studentId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        {props.enrollment.student && (
          <>
            {props.enrollment.student.lastName}, {props.enrollment.student.firstName}
          </>
        )}
      </DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.enrollment.student}
        getOptionLabel={(option) => `${option.lastName}, ${option.firstName}`}
        onChange={(e, value) => {
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.studentId = value?.id;
              draft.student = value;
            })
          );
        }}
        fetchFunction={(searchString: string) => ApiService.search.students(searchString)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />
    );
  };

  const studentIdField = (id: number, customIdType: StudentCustomIdType) => {
    const propertyName = `customId${id}`;
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={customIdType.name}>
        <Typography>
          {props.enrollment.student![propertyName]}
        </Typography>
      </DataDisplay>
    ) : (
      <Stack spacing="sm">
        <Input
          label={customIdType.name}
          disabled
          required={customIdType.isRequired}
          errors={props.validationErrors[propertyName]}
          maxLength={property?.maxLength}
          value={props.enrollment.student![propertyName]}
        />
      </Stack>
    );
  };

  const yearField = () => {
    const propertyName = pn<StudentEnrollment>("year");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatSchoolYear(props.enrollment.year)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.schoolYears?.find((x) => x.id === props.enrollment.year)}
        options={props.editResource.schoolYears}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.year = value?.id;
            })
          )
        }
      />
    );
  };

  const schoolField = () => {
    const propertyName = pn<StudentEnrollment>("schoolId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={schoolDisplayField?.displayName}>{props.enrollment.school?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={schoolDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.schools?.find((x) => x.id === props.enrollment.schoolId)}
        options={props.editResource.schools}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.schoolId = value?.id;
            })
          )
        }
      />
    );
  };

  const gradeLevelField = () => {
    const propertyName = pn<StudentEnrollment>("gradeLevelId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={gradeLevelDisplayField?.displayName}>{props.enrollment.gradeLevel?.name}</DataDisplay>
    ) : (
      <Select
        label={gradeLevelDisplayField?.displayName}
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.gradeLevels?.find((x) => x.id === props.enrollment.gradeLevelId)}
        options={props.editResource.gradeLevels}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.gradeLevelId = value?.id;
              draft.gradeLevelTermId = undefined; // Assuming this is required as per your snippet
            })
          )
        }
      />
    );
  };

  const endGradeLevelField = () => {
    const propertyName = pn<StudentEnrollment>("endGradeLevelId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={endGradeLevelDisplayField?.displayName}>{props.enrollment.endGradeLevel?.name}</DataDisplay>
    ) : (
      <Select
        label={endGradeLevelDisplayField?.displayName}
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.gradeLevels?.find((x) => x.id === props.enrollment.endGradeLevelId)}
        options={props.editResource.gradeLevels}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.endGradeLevelId = value?.id;
            })
          )
        }
      />
    );
  };

  const gradeLevelTermField = () => {
    const propertyName = pn<StudentEnrollment>("gradeLevelTermId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.enrollment.gradeLevelTerm?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.gradeLevelTerms?.find((x) => x.id === props.enrollment.gradeLevelTermId)}
        options={props.editResource.gradeLevelTerms}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.gradeLevelTermId = value?.id;
            })
          )
        }
      />
    );
  };

  const startDateField = () => {
    const propertyName = pn<StudentEnrollment>("startDate");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={startDateDisplayField?.displayName}>{formatDate(props.enrollment.startDate)}</DataDisplay>
    ) : (
      <DatePicker
        modelProperty={property}
        label={startDateDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={props.enrollment.startDate}
        onChange={(value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.startDate = value;
            })
          )
        }
      />
    );
  };

  const endDateField = () => {
    const propertyName = pn<StudentEnrollment>("endDate");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatDate(props.enrollment.endDate)}</DataDisplay>
    ) : (
      <DatePicker
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.enrollment.endDate}
        onChange={(value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.endDate = value;
            })
          )
        }
      />
    );
  };

  const endReasonField = () => {
    const propertyName = pn<StudentEnrollment>("endReasonId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.enrollment.endReason?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        required
        errors={props.validationErrors[propertyName]}
        value={props.editResource.endReasons?.find((x) => x.id === props.enrollment.endReasonId)}
        options={props.editResource.endReasons}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.endReasonId = value?.id;
            })
          )
        }
      />
    );
  };

  const diplomaTypeField = () => {
    const propertyName = pn<StudentEnrollment>("diplomaTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.enrollment.diplomaType?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        //required
        errors={props.validationErrors[propertyName]}
        value={props.editResource.diplomaTypes?.find((x) => x.id === props.enrollment.diplomaTypeId)}
        options={props.editResource.diplomaTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.diplomaTypeId = value?.id;
            })
          )
        }
      />
    );
  };


  const collegeField = () => {
    const propertyName = pn<StudentEnrollment>("collegeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.enrollment.college?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.colleges?.find((x) => x.id === props.enrollment.collegeId)}
        options={props.editResource.colleges}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.collegeId = value?.id;
            })
          )
        }
      />
    );
  };

  const advisorField = () => {
    const propertyName = pn<StudentEnrollment>("advisorId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>
        {props.enrollment.advisor && (
          <>
            {formatAdvisorName(props.enrollment.advisor)}
          </>
        )}
      </DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={property?.displayName}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.advisors?.find((x) => x.id === props.enrollment.advisorId)}
        options={props.editResource.advisors}
        getOptionLabel={formatAdvisorName}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.advisorId = value?.id;
            })
          )
        }
      />
    );
  };

  const iepField = () => {
    const propertyName = pn<StudentEnrollment>("iepId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.enrollment.iep?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.ieps?.find((x) => x.id === props.enrollment.iepId)}
        options={props.editResource.ieps}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.iepId = value?.id;
            })
          )
        }
      />
    );
  };

  const lepField = () => {
    const propertyName = pn<StudentEnrollment>("lepId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.enrollment.lep?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.leps?.find((x) => x.id === props.enrollment.lepId)}
        options={props.editResource.leps}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.lepId = value?.id;
            })
          )
        }
      />
    );
  };

  const initialEnrollmentStatusField = () => {
    const propertyName = pn<StudentEnrollment>("initialEnrollmentStatusId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.enrollment.initialEnrollmentStatus?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.enrollmentStatuses?.find((x) => x.id === props.enrollment.initialEnrollmentStatusId)}
        options={props.editResource.enrollmentStatuses}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.initialEnrollmentStatusId = value?.id;
            })
          )
        }
      />
    );
  };

  const enrollmentStatusField = () => {
    const propertyName = pn<StudentEnrollment>("enrollmentStatusId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={enrollmentStatusDisplayField?.displayName}>
        {props.enrollment.enrollmentStatus?.name}
      </DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={enrollmentStatusDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.enrollmentStatuses?.find((x) => x.id === props.enrollment.enrollmentStatusId)}
        options={props.editResource.enrollmentStatuses}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.enrollmentStatusId = value?.id;
            })
          )
        }
      />
    );
  };

  const finalEnrollmentStatusField = () => {
    const propertyName = pn<StudentEnrollment>("finalEnrollmentStatusId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.enrollment.finalEnrollmentStatus?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.enrollmentStatuses?.find((x) => x.id === props.enrollment.finalEnrollmentStatusId)}
        options={props.editResource.enrollmentStatuses}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.finalEnrollmentStatusId = value?.id;
            })
          )
        }
      />
    );
  };

  const participantStatusField = () => {
    const propertyName = pn<StudentEnrollment>("participantStatusId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.enrollment.participantStatus?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        required // if it shows up, it's required
        value={props.editResource.participantStatuses?.find((x) => x.id === props.enrollment.participantStatusId)}
        options={props.editResource.participantStatuses}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.participantStatusId = value?.id;
            })
          )
        }
      />
    );
  };

  const participantLevelField = () => {
    const propertyName = pn<StudentEnrollment>("participantLevelId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.enrollment.participantLevel?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.participantLevels?.find((x) => x.id === props.enrollment.participantLevelId)}
        options={props.editResource.participantLevels}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.participantLevelId = value?.id;
            })
          )
        }
      />
    );
  };

  const dualEnrollmentParticipationField = () => {
    const propertyName = pn<StudentEnrollment>("dualEnrollmentParticipationId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.enrollment.dualEnrollmentParticipation?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.dualEnrollmentParticipations?.find((x) => x.id === props.enrollment.dualEnrollmentParticipationId)}
        options={props.editResource.dualEnrollmentParticipations}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.dualEnrollmentParticipationId = value?.id;
            })
          )
        }
      />
    );
  };

  const dualEnrollmentCredentialsField = () => {
    const propertyName = pn<StudentEnrollment>("dualEnrollmentCredentialsId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.enrollment.dualEnrollmentCredentials?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.dualEnrollmentCredentials?.find((x) => x.id === props.enrollment.dualEnrollmentCredentialsId)}
        options={props.editResource.dualEnrollmentCredentials}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.dualEnrollmentCredentialsId = value?.id;
            })
          )
        }
      />
    );
  };

  const workStudyField = () => {
    const propertyName = pn<StudentEnrollment>("workStudyId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.enrollment.workStudy?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.dualEnrollmentCredentials?.find((x) => x.id === props.enrollment.workStudyId)}
        options={props.editResource.workStudyCulturalCommunity}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.workStudyId = value?.id;
            })
          )
        }
      />
    );
  };

  const employmentUBField = () => {
    const propertyName = pn<StudentEnrollment>("employmentUBId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.enrollment.employmentUB?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.employments?.find((x) => x.id === props.enrollment.employmentUBId)}
        options={props.editResource.employments}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.employmentUBId = value?.id;
            })
          )
        }
      />
    );
  };

  const culturalField = () => {
    const propertyName = pn<StudentEnrollment>("culturalId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.enrollment.cultural?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.workStudyCulturalCommunity?.find((x) => x.id === props.enrollment.culturalId)}
        options={props.editResource.workStudyCulturalCommunity}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.culturalId = value?.id;
            })
          )
        }
      />
    );
  };

  const communityServiceField = () => {
    const propertyName = pn<StudentEnrollment>("communityServiceId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.enrollment.communityService?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.workStudyCulturalCommunity?.find((x) => x.id === props.enrollment.communityServiceId)}
        options={props.editResource.workStudyCulturalCommunity}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.communityServiceId = value?.id;
            })
          )
        }
      />
    );
  };

  const lepInstructionField = () => {
    const propertyName = pn<StudentEnrollment>("lepInstructionId");
    const property = properties.find((x) => x.propertyName.toLocaleLowerCase() === propertyName.toLocaleLowerCase());

    return props.crudMode === "view" ? (
      <DataDisplay label={lepInstructionDisplayField?.displayName}>{props.enrollment.lepInstruction?.name}</DataDisplay>
    ) : (
      <Select
        label={lepInstructionDisplayField?.displayName}
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.workStudyCulturalCommunity?.find((x) => x.id === props.enrollment.lepInstructionId)}
        options={props.editResource.workStudyCulturalCommunity}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.lepInstructionId = value?.id;
            })
          )
        }
      />
    );
  };

  const militaryConnectionField = () => {
    const propertyName = pn<StudentEnrollment>("militaryConnectionId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.enrollment.militaryConnection?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.militaryConnections?.find((x) => x.id === props.enrollment.militaryConnectionId)}
        options={props.editResource.militaryConnections}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.militaryConnectionId = value?.id;
            })
          )
        }
      />
    );
  };

  const persistantStatusField = () => {
    const propertyName = pn<StudentEnrollment>("persistantStatusId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.enrollment.persistantStatus?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.persistantStatuses?.find((x) => x.id === props.enrollment.persistantStatusId)}
        options={props.editResource.persistantStatuses}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.persistantStatusId = value?.id;
            })
          )
        }
      />
    );
  };

  const freeReducedLunchField = () => {
    const propertyName = pn<StudentEnrollment>("freeReducedLunch");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.enrollment.freeReducedLunch)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.enrollment.freeReducedLunch)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.freeReducedLunch = value?.id;
            })
          )
        }
      />
    );
  };

  const magnetField = () => {
    const propertyName = pn<StudentEnrollment>("magnet");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.enrollment.magnet)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.enrollment.magnet)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.magnet = value?.id;
            })
          )
        }
      />
    );
  };

  const heldBackField = () => {
    const propertyName = pn<StudentEnrollment>("heldBack");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.enrollment.heldBack)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.enrollment.heldBack)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.heldBack = value?.id;
            })
          )
        }
      />
    );
  };

  const avidField = () => {
    const propertyName = pn<StudentEnrollment>("avid");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.enrollment.avid)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.enrollment.avid)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.avid = value?.id;
            })
          )
        }
      />
    );
  };

  const mediaReleaseField = () => {
    const propertyName = pn<StudentEnrollment>("mediaRelease");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.enrollment.mediaRelease)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.enrollment.mediaRelease)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.mediaRelease = value?.id;
            })
          )
        }
      />
    );
  };

  const cacgField = () => {
    const propertyName = pn<StudentEnrollment>("cacg");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.enrollment.cacg)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.enrollment.cacg)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.cacg = value?.id;
            })
          )
        }
      />
    );
  };

  const twentyFirstCenturyField = () => {
    const propertyName = pn<StudentEnrollment>("twentyFirstCentury");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.enrollment.twentyFirstCentury)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.enrollment.twentyFirstCentury)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.twentyFirstCentury = value?.id;
            })
          )
        }
      />
    );
  };

  const dropTransferFormField = () => {
    const propertyName = pn<StudentEnrollment>("dropTransferForm");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.enrollment.dropTransferForm)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.enrollment.dropTransferForm)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.dropTransferForm = value?.id;
            })
          )
        }
      />
    );
  };

  const homelessField = () => {
    const propertyName = pn<StudentEnrollment>("homeless");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.enrollment.homeless)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.enrollment.homeless)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.homeless = value?.id;
            })
          )
        }
      />
    );
  };

  const fosterCareField = () => {
    const propertyName = pn<StudentEnrollment>("fosterCare");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.enrollment.fosterCare)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.enrollment.fosterCare)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.fosterCare = value?.id;
            })
          )
        }
      />
    );
  };

  const atRiskField = () => {
    const propertyName = pn<StudentEnrollment>("atRisk");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.enrollment.atRisk)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.enrollment.atRisk)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.atRisk = value?.id;
            })
          )
        }
      />
    );
  };

  const ubField = () => {
    const propertyName = pn<StudentEnrollment>("ub");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.enrollment.ub)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.enrollment.ub)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.ub = value?.id;
            })
          )
        }
      />
    );
  };

  const ubmsField = () => {
    const propertyName = pn<StudentEnrollment>("ubms");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.enrollment.ubms)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.enrollment.ubms)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.ubms = value?.id;
            })
          )
        }
      />
    );
  };

  const vubField = () => {
    const propertyName = pn<StudentEnrollment>("vub");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.enrollment.vub)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.enrollment.vub)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.vub = value?.id;
            })
          )
        }
      />
    );
  };

  const tsField = () => {
    const propertyName = pn<StudentEnrollment>("ts");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.enrollment.ts)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.enrollment.ts)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.ts = value?.id;
            })
          )
        }
      />
    );
  };

  const sssField = () => {
    const propertyName = pn<StudentEnrollment>("sss");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.enrollment.sss)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.enrollment.sss)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.sss = value?.id;
            })
          )
        }
      />
    );
  };

  const gearUpField = () => {
    const propertyName = pn<StudentEnrollment>("gearUp");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.enrollment.gearUp)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.enrollment.gearUp)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.gearUp = value?.id;
            })
          )
        }
      />
    );
  };

  const eocField = () => {
    const propertyName = pn<StudentEnrollment>("eoc");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.enrollment.eoc)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.enrollment.eoc)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.eoc = value?.id;
            })
          )
        }
      />
    );
  };

  const creditsField = () => {
    const propertyName = pn<StudentEnrollment>("credits");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={creditsDisplayField?.displayName}>{props.enrollment.credits}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        label={creditsDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={props.enrollment.credits}
        onChange={(e) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.credits = e.target.value as unknown as number;
            })
          )
        }
      />
    );
  };

  const nihField = () => {
    const propertyName = pn<StudentEnrollment>("nih");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={nihDisplayField?.displayName}>{props.enrollment.nih}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        label={nihDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={props.enrollment.nih}
        onChange={(e) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.nih = e.target.value as unknown as number;
            })
          )
        }
      />
    );
  };

  const agiField = () => {
    const propertyName = pn<StudentEnrollment>("agi");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={agiDisplayField?.displayName}>{props.enrollment.agi}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        label={agiDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={props.enrollment.agi}
        onChange={(e) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.agi = e.target.value as unknown as number;
            })
          )
        }
      />
    );
  };

  const unmetNeedField = () => {
    const propertyName = pn<StudentEnrollment>("unmetNeed");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={unmetNeedDisplayField?.displayName}>{props.enrollment.unmetNeed}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        label={unmetNeedDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={props.enrollment.unmetNeed}
        onChange={(e) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.unmetNeed = e.target.value as unknown as number;
            })
          )
        }
      />
    );
  };

  const loanAmountField = () => {
    const propertyName = pn<StudentEnrollment>("loanAmount");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={loanAmountDisplayField?.displayName}>{props.enrollment.loanAmount}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        label={loanAmountDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={props.enrollment.loanAmount}
        onChange={(e) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.loanAmount = e.target.value as unknown as number;
            })
          )
        }
      />
    );
  };

  const pellRecipientField = () => {
    const propertyName = pn<StudentEnrollment>("pellRecipient");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={pellRecipientDisplayField?.displayName}>
        {formatBoolean(props.enrollment.pellRecipient)}
      </DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={pellRecipientDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.enrollment.pellRecipient)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.pellRecipient = value?.id;
            })
          )
        }
      />
    );
  };

  const academicStandingField = () => {
    const propertyName = pn<StudentEnrollment>("academicStandingId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={academicStandingDisplayField?.displayName}>
        {props.enrollment.academicStanding?.name}
      </DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={academicStandingDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.academicStandings?.find((x) => x.id === props.enrollment.academicStandingId)}
        options={props.editResource.academicStandings}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.academicStandingId = value?.id;
            })
          )
        }
      />
    );
  };

  const ccpField = () => {
    const propertyName = pn<StudentEnrollment>("ccpId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={ccpDisplayField?.displayName}>{props.enrollment.ccp?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={ccpDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.ccps?.find((x) => x.id === props.enrollment.ccpId)}
        options={props.editResource.ccps}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.ccpId = value?.id;
            })
          )
        }
      />
    );
  };

  const rigorousCField = () => {
    const propertyName = pn<StudentEnrollment>("rigorousCId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={rigorousCDisplayField?.displayName}>{props.enrollment.rigorousC?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        label={rigorousCDisplayField?.displayName}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.rigorousCs?.find((x) => x.id === props.enrollment.rigorousCId)}
        options={props.editResource.rigorousCs}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.rigorousCId = value?.id;
            })
          )
        }
      />
    );
  };

  const dualEnrollmentField = () => {
    const propertyName = pn<StudentEnrollment>("dualEnrollment");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatBoolean(props.enrollment.dualEnrollment)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={BOOLEAN_SELECT_LIST_ITEMS.find((x) => x.id === props.enrollment.dualEnrollment)}
        options={BOOLEAN_SELECT_LIST_ITEMS}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setEnrollment(
            produce(props.enrollment, (draft) => {
              draft.dualEnrollment = value?.id;
            })
          )
        }
      />
    );
  };

  return (
    <Stack ref={breakpointsRef} spacing="xl">
      {/* Info */}
      <Box>
        <Grid container spacing="sm">
          {!props.hideStudent && (
            <Grid item xs={smallFieldBreakpoints}>
              {studentField()}
            </Grid>
          )}

          {props.enrollment && props.enrollment.student && (
            <>
              {props.viewResource.customIdTypes?.map((x) => (
                <Grid key={x.id} item xs={smallFieldBreakpoints}>
                  {studentIdField(x.id, x)}
                </Grid>
              ))
              }
            </>
          )}

          <Grid item xs={smallFieldBreakpoints}>
            {yearField()}
          </Grid>

          <Grid item xs={smallFieldBreakpoints}>
            {schoolField()}
          </Grid>

          <Grid item xs={smallFieldBreakpoints}>
            {gradeLevelField()}
          </Grid>

          {endGradeLevelDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {endGradeLevelField()}
            </Grid>
          )}

          {showGradeLevelTerm && (
            <Grid item xs={smallFieldBreakpoints}>
              {gradeLevelTermField()}
            </Grid>
          )}

          {enrollmentStatusDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {enrollmentStatusField()}
            </Grid>
          )}

          {creditsDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {creditsField()}
            </Grid>
          )}

          {advisorDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {advisorField()}
            </Grid>
          )}

          {/* WSUTRIO wants to hide this for "New Summer Participant", all other Clients want this, so we created this special condition for them */}
          {(props.enrollment.participantStatusId != 99 ) && (
            <Grid item xs={smallFieldBreakpoints}>
              {startDateField()}
            </Grid>
          )}

          <Grid item xs={smallFieldBreakpoints}>
            {endDateField()}
          </Grid>

          {props.enrollment.endDate && (
            <Grid item xs={smallFieldBreakpoints}>
              {endReasonField()}
            </Grid>
          )}

          {diplomaTypeDisplayField && graduatedEndReasons?.some(e => e.id == props.enrollment.endReasonId) && (
            <Grid item xs={smallFieldBreakpoints}>
              {diplomaTypeField()}
            </Grid>
          )}

          {academicStandingDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {academicStandingField()}
            </Grid>
          )}

          {collegeDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {collegeField()}
            </Grid>
          )}

          {participantLevelDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {participantLevelField()}
            </Grid>
          )}

          {dualEnrollmentParticipationDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {dualEnrollmentParticipationField()}
            </Grid>
          )}

          {dualEnrollmentCredentialsDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {dualEnrollmentCredentialsField()}
            </Grid>
          )}

          {workStudyDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {workStudyField()}
            </Grid>
          )}

          {employmentDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {employmentUBField()}
            </Grid>
          )}

          {culturalDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {culturalField()}
            </Grid>
          )}

          {communityServiceDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {communityServiceField()}
            </Grid>
          )}
          <GridBreak />
        </Grid>
      </Box>

      {/* Variables */}
      <Box>
        <Grid container spacing="sm">
          {iepDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {iepField()}
            </Grid>
          )}

          {lepDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {lepField()}
            </Grid>
          )}

          {lepInstructionDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {lepInstructionField()}
            </Grid>
          )}

          {initialEnrollmentStatusDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {initialEnrollmentStatusField()}
            </Grid>
          )}

          {finalEnrollmentStatusDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {finalEnrollmentStatusField()}
            </Grid>
          )}

          {participantStatusDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {participantStatusField()}
            </Grid>
          )}

          {dependentStatusDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {dependentStatusField()}
            </Grid>
          )}

          {nihDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {nihField()}
            </Grid>
          )}

          {agiDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {agiField()}
            </Grid>
          )}

          {unmetNeedDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {unmetNeedField()}
            </Grid>
          )}

          {loanAmountDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {loanAmountField()}
            </Grid>
          )}

          {pellRecipientDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {pellRecipientField()}
            </Grid>
          )}

          {persistantStatusDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {persistantStatusField()}
            </Grid>
          )}

          {militaryConnectionDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {militaryConnectionField()}
            </Grid>
          )}

          {freeReducedLunchDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {freeReducedLunchField()}
            </Grid>
          )}

          {magnetDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {magnetField()}
            </Grid>
          )}

          {heldBackDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {heldBackField()}
            </Grid>
          )}

          {avidDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {avidField()}
            </Grid>
          )}

          {mediaReleaseDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {mediaReleaseField()}
            </Grid>
          )}

          {cacgDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {cacgField()}
            </Grid>
          )}

          {twentyFirstCenturyDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {twentyFirstCenturyField()}
            </Grid>
          )}

          {dropTransferFormDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {dropTransferFormField()}
            </Grid>
          )}

          {homelessDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {homelessField()}
            </Grid>
          )}

          {fosterCareDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {fosterCareField()}
            </Grid>
          )}

          {atRiskDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {atRiskField()}
            </Grid>
          )}

          {ubDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {ubField()}
            </Grid>
          )}

          {ubmsDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {ubmsField()}
            </Grid>
          )}

          {vubDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {vubField()}
            </Grid>
          )}

          {tsDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {tsField()}
            </Grid>
          )}

          {sssDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {sssField()}
            </Grid>
          )}

          {gearUpDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {gearUpField()}
            </Grid>
          )}

          {rigorousCDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {rigorousCField()}
            </Grid>
          )}

          {ccpDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {ccpField()}
            </Grid>
          )}

          {eocDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {eocField()}
            </Grid>
          )}

          {dualEnrollmentDisplayField && (
            <Grid item xs={smallFieldBreakpoints}>
              {dualEnrollmentField()}
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Custom Entities */}
      <Box>
        <Grid container spacing="sm">
          {props.viewResource.customDataModelFields?.map((field) => {
            return (
              <Grid key={field.id} item xs={smallFieldBreakpoints}>
                {props.crudMode === "view" && (
                  <DataDisplay label={field.name}>
                    <CustomDataModelFieldDisplay field={field} dataModel={props.enrollment} />
                  </DataDisplay>
                )}

                {props.crudMode === "edit" && (
                  <CustomDataModelFieldEditor
                    field={field}
                    dataModel={props.enrollment}
                    setDataModel={(studentEnrollment) => props.setEnrollment(studentEnrollment)}
                    errors={props.validationErrors[field.fieldNameCamelCase!]}
                  />
                )}
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Stack>
  );
};
